import { ActionIcon, Button, Group, Menu, Text } from '@mantine/core';
import { useRef } from 'react';
import { TbFileUpload, TbFile } from 'react-icons/tb';
import { FiTrash2, FiDownload } from 'react-icons/fi';
import { useFileSelectEditor } from './use-file-select-editor';
import { useTranslation } from 'react-i18next';
import { useFetchUserProfile } from '@/api';
import { RecentFile } from '@/types';
import { useStore } from '@/store';

const formatDate = (date: Date) => {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface RecentFileItemProps {
  file: RecentFile;
  onSelect: (fileId: string) => void;
  onDownload: (fileId: string) => void;
  onDelete: (fileId: string) => void;
}

const RecentFileMenuItem = ({
  file,
  onSelect,
  onDownload,
  onDelete,
}: RecentFileItemProps) => {
  const { t } = useTranslation();
  const { data: userProfile, isLoading } = useFetchUserProfile(file.userId);
  const formattedDate = formatDate(file.usedAt);

  const handleDownloadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDownload(file.fileId);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDelete(file.fileId);
  };

  const handleSelectKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    onSelect(file.fileId);
  };

  return (
    <Menu.Item
      component="div"
      onClick={() => onSelect(file.fileId)}
      onKeyDownCapture={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
        }
      }}
      onKeyUp={handleSelectKeyUp}
      icon={<TbFile size={16} />}
      rightSection={
        <Group spacing={4} noWrap>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={handleDownloadClick}
            aria-label={t('button.downloadRecentFile')}
          >
            <FiDownload size={14} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            onClick={handleDeleteClick}
            aria-label={t('button.removeFromRecentFiles')}
          >
            <FiTrash2 size={14} />
          </ActionIcon>
        </Group>
      }
      sx={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
    >
      <div style={{ width: '100%', minWidth: 0 }}>
        <Text
          size="sm"
          style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
        >
          {file.fileName}
        </Text>
        <Text
          size="xs"
          color="dimmed"
          style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
        >
          {isLoading
            ? 'Loading...'
            : userProfile?.name
              ? `${userProfile.name} • ${formattedDate}`
              : formattedDate}
        </Text>
      </div>
    </Menu.Item>
  );
};

export const FileSelector = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentFile = useStore(({ config: c }) => c.file);
  const {
    handleFileSelect,
    handleRecentFileSelect,
    recentFiles,
    handleRecentFileDownload,
    handleRecentFileDelete,
    getAcceptedFileExtensionsForInput,
  } = useFileSelectEditor();

  // Get the display text - show file name if available, otherwise show "Select File"
  const getButtonText = () => {
    if (currentFile) {
      // Truncate long file names to fit in the button (max ~40 chars)
      const maxLength = 40;
      if (currentFile.name.length > maxLength) {
        return currentFile.name.substring(0, maxLength - 3) + '...';
      }
      return currentFile.name;
    }
    return t('menu.file.selectFile');
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptedFileExtensionsForInput()}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      <Menu shadow="md" width={500} position="bottom">
        <Menu.Target>
          <Button
            variant="default"
            leftIcon={<TbFileUpload size={18} />}
            sx={{
              cursor: 'pointer',
              maxWidth: '400px',
            }}
            title={currentFile?.name || t('menu.file.selectFile')}
          >
            {getButtonText()}
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            icon={<TbFileUpload size={16} />}
            onClick={handleUploadClick}
          >
            {t('menu.file.uploadNew')}
          </Menu.Item>
          {recentFiles.length > 0 && (
            <>
              <Menu.Divider />
              <Menu.Label>{t('title.recentFiles')}</Menu.Label>
              {recentFiles.map((file) => (
                <RecentFileMenuItem
                  key={file.fileId}
                  file={file}
                  onSelect={handleRecentFileSelect}
                  onDownload={handleRecentFileDownload}
                  onDelete={handleRecentFileDelete}
                />
              ))}
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </>
  );
};
